<template>
  <div class="musubi-page musubi-page--drips">
    <header class="musubi-page__head">
      <h1>Drip flows</h1>
      <p class="text-muted">
        Automated email sequences. A flow has a trigger (manual, or someone
        joining a list) and an ordered list of steps — send an email, wait,
        or a <strong>branch</strong> that splits depending on whether the
        recipient opened or clicked the previous email. Yorlin advances
        enrolled subscribers through an active flow's steps automatically.
      </p>
    </header>

    <div v-if="error" class="alert alert-danger py-2 small">{{ error }}</div>

    <section class="actions-bar">
      <button class="btn btn-outline-primary" @click="openCreate">
        <CygnusIcon name="plus" class="me-1" />New flow
      </button>
      <button
        class="btn btn-outline-secondary"
        :disabled="busy.tick"
        title="Development only — advances enrolled subscribers right now instead of waiting for the automatic background run."
        @click="tick"
      >
        <CygnusIcon name="spinner" :spin="true" class="me-1" v-if="busy.tick" />
        <CygnusIcon name="bolt" class="me-1" v-else />Advance flows now (dev)
      </button>
      <button class="btn btn-outline-secondary" :disabled="busy.sync" @click="syncAudiences"
        title="Enrol prospects matching active flows' audience filters now (also runs automatically every 15 min)">
        <CygnusIcon name="spinner" :spin="true" class="me-1" v-if="busy.sync" />
        <CygnusIcon name="users" class="me-1" v-else />Sync audiences
      </button>
      <button class="btn btn-link" :disabled="busy.load" @click="refresh">
        <CygnusIcon name="spinner" :spin="true" v-if="busy.load" />
        <span v-else><CygnusIcon name="refresh" /> Refresh</span>
      </button>
      <a class="btn btn-link ms-auto" href="/acquire/pipeline-wizard" title="Set up a crawl → audience → flow pipeline in one guided flow">
        <CygnusIcon name="diagram" class="me-1" />Guided setup
      </a>
      <span v-if="lastTick" class="actions-bar__msg">{{ lastTick }}</span>
    </section>

    <!-- Task #54 — signal -> outreach review queue (pending trigger matches) -->
    <TriggerReviewQueue class="mb-3" @changed="onQueueChanged" />

    <!-- Editor (create or edit) -->
    <section v-if="editor" class="editor card mb-3">
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-start mb-3">
          <h6 class="mb-0">
            {{ editor.id ? `Edit flow #${editor.id}` : 'New flow' }}
            <span v-if="editor.id" :class="['status-pill', `status-pill--${editor.status}`]">{{ editor.status }}</span>
          </h6>
          <button class="btn btn-link btn-sm" @click="closeEditor">Close</button>
        </div>

        <!-- DISCOVERY MODE banner: gathering + counting, not sending yet. -->
        <div v-if="editor.id && editor.status === 'discovering'" class="disc-banner">
          <div class="disc-banner__head"><CygnusIcon name="search" />Discovering — not sending yet</div>
          <div class="disc-banner__sub">
            The linked crawler keeps finding + enriching prospects and we count who would qualify — but nothing enrols and no email sends until you go live.
          </div>
          <div class="disc-counts">
            <div class="disc-count">
              <div class="disc-count__value">{{ discovery ? fmt(discovery.discovered) : '—' }}</div>
              <div class="disc-count__label">Discovered</div>
            </div>
            <div class="disc-count">
              <div class="disc-count__value">{{ discovery ? fmt(discovery.enriched) : '—' }}</div>
              <div class="disc-count__label">Enriched</div>
            </div>
            <div class="disc-count disc-count--eligible">
              <div class="disc-count__value">{{ discovery ? fmt(discovery.would_be_eligible) : '—' }}<span v-if="discovery && discovery.capped">+</span></div>
              <div class="disc-count__label">Would enrol</div>
            </div>
          </div>
        </div>

        <!-- Go-live confirmation (opened from "Go live" on a discovering flow). -->
        <div v-if="editor.id && editor.status === 'discovering' && goLiveOpen" class="golive-panel">
          <div class="golive-panel__title"><CygnusIcon name="rocket" class="me-1" />Start sending?</div>
          <div class="golive-panel__note">{{ discovery ? discovery.go_live_note : 'Loading the latest match count…' }}</div>
          <div class="d-flex gap-2">
            <button type="button" class="btn btn-success btn-sm" :disabled="busy.golive" @click="confirmGoLive">
              <CygnusIcon name="spinner" :spin="true" class="me-1" v-if="busy.golive" />
              <CygnusIcon name="check" class="me-1" v-else />Go live &amp; start sending
            </button>
            <button type="button" class="btn btn-outline-secondary btn-sm" @click="goLiveOpen = false">Keep discovering</button>
          </div>
        </div>

        <form @submit.prevent="saveFlow">
          <div class="row g-2 mb-3">
            <div class="col-md-5">
              <label class="form-label small text-muted">Name *</label>
              <input v-model="editor.name" type="text" required class="form-control form-control-sm" placeholder="Welcome sequence">
            </div>
            <div class="col-md-4">
              <label class="form-label small text-muted">Trigger</label>
              <select v-model="editor.trigger_type" class="form-select form-select-sm">
                <option value="manual">Manual (enroll by hand / API)</option>
                <option value="list_join">List join</option>
                <option value="form_submit">Form submit (P5)</option>
                <option value="site_visit">Website visit — New</option>
                <option value="event" disabled>Event (coming soon — no dispatch yet)</option>
              </select>
            </div>
            <div class="col-md-3">
              <label class="form-label small text-muted">Sender identity</label>
              <MusubiEntitySelect
                v-model="editor.sender_identity_id"
                endpoint="/api/v1/admin/senders"
                :option-label="s => s.label || `${s.from_name} <${s.from_email}>`"
                placeholder="Default sender" />
            </div>
            <div v-if="editor.trigger_type === 'list_join'" class="col-md-4">
              <label class="form-label small text-muted">Trigger list *</label>
              <MusubiEntitySelect
                v-model="editor.trigger_list_id"
                endpoint="/api/v1/admin/lists"
                :option-label="l => l.name"
                placeholder="List that fires this flow" required />
            </div>
            <div class="col-12">
              <label class="form-label small text-muted">Description</label>
              <input v-model="editor.description" type="text" class="form-control form-control-sm">
            </div>
          </div>

          <!-- Website-visit trigger config (Visitor-ID slice 2) -->
          <div v-if="editor.trigger_type === 'site_visit'" class="sv-cfg mb-3">
            <div class="sv-cfg__head">
              <CygnusIcon name="globe" />
              <div>
                <div class="sv-cfg__title">Trigger: Website visit <span class="sv-new">New</span></div>
                <div class="sv-cfg__sub">Start this flow when a company is identified visiting your site. Only identified visits fire this trigger — anonymous traffic is never enrolled.</div>
              </div>
            </div>

            <div class="sv-field">
              <label class="form-label small fw-semibold">Minimum confidence</label>
              <div class="sv-seg">
                <button type="button" :class="{ on: editor.sv_min_confidence === 'high' }" @click="editor.sv_min_confidence = 'high'">High only</button>
                <button type="button" :class="{ on: editor.sv_min_confidence === 'medium' }" @click="editor.sv_min_confidence = 'medium'">High + Medium</button>
              </div>
              <div class="sv-foot">Recommended: High only. Medium-confidence matches come from network signals (rDNS / ASN) and can misattribute shared or ISP ranges.</div>
            </div>

            <div class="sv-field">
              <label class="form-label small fw-semibold">Auto-enrol identified visitors</label>
              <div class="sv-toggle-row">
                <label class="sv-switch">
                  <input type="checkbox" v-model="editor.sv_auto_enroll">
                  <span class="track"></span>
                </label>
                <div>
                  <div class="sv-toggle-title">
                    {{ editor.sv_auto_enroll ? 'On — enrol matches automatically' : 'Off — send to review queue first' }}
                    <span v-if="!editor.sv_auto_enroll" class="sv-default">Default</span>
                  </div>
                  <div class="sv-toggle-desc">Matches wait for a human to approve before entering the flow. Turn on only once you trust the matching for this audience.</div>
                  <div v-if="editor.sv_auto_enroll" class="sv-caution">
                    <CygnusIcon name="warning" />
                    <span>Emailing off a single anonymous visit risks contacting people who never opted in. Keep auto-enrol off unless the visitor is a known, engaged contact.</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="sv-field">
              <label class="form-label small fw-semibold">Only enrol visitors matching</label>
              <div class="sv-grid">
                <div>
                  <div class="sv-foot mb-1">Sector</div>
                  <MusubiSectorSelect v-model="editor.sv_sectors" compact placeholder="any sector" style="min-width:150px" />
                </div>
                <div>
                  <div class="sv-foot mb-1">Region</div>
                  <input v-model="editor.sv_region" type="text" list="dl-regions" class="form-control form-control-sm" placeholder="anywhere">
                </div>
              </div>
            </div>
          </div>

          <!-- Audience — auto-enrol matching prospects (when the flow is active) -->
          <div class="audience-box mb-3">
            <div class="d-flex align-items-center mb-1 flex-wrap gap-2">
              <label class="form-label small text-muted mb-0"><CygnusIcon name="users" class="me-1" />Audience — auto-enrol prospects</label>
              <span v-if="audiencePreview" class="badge bg-info-subtle text-info-emphasis border">
                {{ audiencePreview.matching }} match{{ audiencePreview.capped ? '+' : '' }} · {{ audiencePreview.new }} not yet enrolled
              </span>
              <div class="ms-auto d-flex gap-2">
                <button type="button" class="btn btn-sm btn-outline-secondary py-0 px-2" :disabled="!editor.audience.length || busy.preview" @click="previewAudience">
                  <CygnusIcon name="spinner" :spin="true" class="me-1" v-if="busy.preview" /><CygnusIcon name="search" class="me-1" v-else />Preview match
                </button>
                <button type="button" class="btn btn-sm btn-outline-secondary py-0 px-2" @click="addAudienceSource">
                  <CygnusIcon name="plus" class="me-1" />Add source
                </button>
              </div>
            </div>
            <p v-if="!editor.audience.length" class="text-muted small fst-italic mb-1">
              No audience — this flow only enrols by manual selection (Prospects → Add to flow). Add a source to auto-enrol prospects matching its filters (and keep catching new ones). Unsubscribed prospects are always excluded, and no one is enrolled twice.
            </p>
            <div v-for="(src, si) in editor.audience" :key="si" class="audience-source">
              <span class="small text-muted me-1">Prospects where</span>
              <select v-model="src.temperature" class="form-select form-select-sm w-auto">
                <option value="">any temp</option>
                <option value="warm">warm</option>
                <option value="cold">cold</option>
              </select>
              <select v-model="src.stage" class="form-select form-select-sm w-auto">
                <option value="">any stage</option>
                <option value="new">new</option>
                <option value="qualifying">qualifying</option>
                <option value="qualified">qualified</option>
                <option value="promoted">promoted</option>
              </select>
              <input v-model="src.framework" type="text" list="dl-frameworks" class="form-control form-control-sm" placeholder="framework (any)" style="max-width:140px">
              <input v-model="src.region" type="text" list="dl-regions" class="form-control form-control-sm" placeholder="region (any)" style="max-width:120px">
              <MusubiSectorSelect v-model="src.sectors" compact placeholder="any industry" style="min-width:150px" />
              <select v-model="src.company_status" class="form-select form-select-sm w-auto" title="Companies House status">
                <option value="">any status</option>
                <option value="active">active</option>
                <option value="dissolved">dissolved</option>
                <option value="liquidation">liquidation</option>
                <option value="administration">administration</option>
              </select>
              <span class="small text-muted">staff ≥</span>
              <input v-model.number="src.min_employees" type="number" min="0" class="form-control form-control-sm" placeholder="0" style="max-width:80px">
              <span class="small text-muted">score ≥</span>
              <input v-model.number="src.min_score" type="number" min="0" class="form-control form-control-sm" placeholder="0" style="max-width:80px">
              <label class="small ms-1"><input type="checkbox" v-model="src.leads_only"> shows intent</label>
              <button type="button" class="btn btn-outline-danger btn-sm ms-auto" @click="editor.audience.splice(si, 1)"><CygnusIcon name="trash" /></button>
            </div>
            <p v-if="editor.audience.length && editor.status !== 'active'" class="text-muted small fst-italic mt-1 mb-0">
              Auto-enrol runs only while the flow is <strong>active</strong> — activate it below to start.
            </p>
            <datalist id="dl-frameworks"><option v-for="f in frameworks" :key="f" :value="f"></option></datalist>
            <datalist id="dl-regions"><option v-for="r in regions" :key="r" :value="r"></option></datalist>
          </div>

          <!-- Crawl source — the crawler feeding this pipeline (link / swap / edit) -->
          <div class="source-box mb-3">
            <div class="d-flex align-items-center mb-1 flex-wrap gap-2">
              <label class="form-label small text-muted mb-0"><CygnusIcon name="broadcast" class="me-1" />Crawl source — the crawler behind this pipeline</label>
            </div>
            <div v-if="sourcesForbidden" class="text-muted small fst-italic mb-0">
              <CygnusIcon name="lock" class="me-1" />Editing crawl sources needs Acquire access — ask an operator with that permission.
            </div>
            <template v-else>
              <div class="d-flex align-items-center gap-2 flex-wrap">
                <select v-model="editor.source_id" class="form-select form-select-sm w-auto" @change="closeSourceEdit">
                  <option :value="null">— No source linked —</option>
                  <option v-for="s in pickableSources" :key="s.id" :value="s.id">
                    {{ s.name || ('#' + s.id) }} · {{ sourceTypeLabel(s.type) }}{{ s.archived_at ? ' (archived)' : '' }}
                  </option>
                </select>
                <button v-if="linkedSource" type="button" class="btn btn-sm btn-outline-secondary py-0 px-2" @click="toggleSourceEdit">
                  <CygnusIcon name="gear" class="me-1" />{{ sourceEditOpen ? 'Close' : 'Edit parameters' }}
                </button>
                <span class="small text-muted">Swapping only re-points this pipeline — the old source is left intact for others.</span>
              </div>
              <p v-if="!editor.source_id" class="text-muted small fst-italic mt-1 mb-0">
                No crawler is linked. Link one so this pipeline knows which discovery feed it belongs to. (Auto-enrol above still runs off the audience filters.)
              </p>
              <div v-if="sourceEditOpen && linkedSource" class="source-edit mt-2">
                <div v-if="sourceMsg" class="alert py-1 px-2 small mb-2" :class="sourceMsgOk ? 'alert-success' : 'alert-warning'">{{ sourceMsg }}</div>
                <CrawlSourceFields
                  :key="'srcedit-' + linkedSource.id"
                  :api="api"
                  :initial-type="linkedSource.type"
                  :initial-config="linkedSource.config || {}"
                  :initial-name="linkedSource.name || ''"
                  @change="onSourceFieldsChange"
                />
                <div class="mt-2">
                  <button type="button" class="btn btn-sm btn-primary" :disabled="savingSource || !sourceDraft.valid" @click="saveSourceParams">
                    <CygnusIcon name="spinner" :spin="true" class="me-1" v-if="savingSource" />Save source changes
                  </button>
                  <span class="small text-muted ms-2">Updates the crawl source itself (its type can't change — recreate to switch type).</span>
                </div>
              </div>
            </template>
          </div>

          <!-- Flow-level send window (steps inherit this; a step can override it) -->
          <div class="mb-3">
            <label class="form-label small text-muted mb-1"><CygnusIcon name="clock" class="me-1" />Flow send window</label>
            <SendWindowEditor
              v-model="editor.send_window"
              label="Override this flow's send window"
              inherited-label="workspace window"
              :inherited-summary="workspaceWindowSummary"
              :default-timezone="workspaceTimezone"
            />
            <div class="form-text small">Applies to every send step in this flow. When off, sends inherit the workspace window. A single step can still override this below.</div>
          </div>

          <!-- Flow-level send-time optimisation (steps inherit this mode) -->
          <div class="mb-3">
            <label class="form-label small text-muted mb-1"><CygnusIcon name="bolt" class="me-1" />Send-time optimisation</label>
            <SendTimingToggle v-model="editor.send_timing" :entitled="stoEntitled" />
            <div class="form-text small">Applies to every send step in this flow.</div>
          </div>

          <!-- Step builder -->
          <label class="form-label small text-muted mb-2">Steps</label>
          <div v-if="templateHint" class="alert alert-info py-1 px-2 small d-flex align-items-center gap-2 mb-2">
            <CygnusIcon name="info" />
            <span class="flex-grow-1">{{ templateHint }}</span>
            <button type="button" class="btn btn-sm btn-link p-0" @click="templateHint = ''">Dismiss</button>
          </div>
          <div v-if="(editor.steps || []).length === 0" class="text-muted small fst-italic mb-2">
            No steps — add a Send or Wait below. An empty flow completes immediately on enrol.
          </div>

          <draggable
            v-model="editor.steps"
            item-key="_k"
            handle=".step-drag"
            :group="{ name: 'dripSteps' }"
            :move="onStepMove"
            @change="normalizeTop"
            class="drip-steps"
            ghost-class="step-ghost"
            tag="div"
          >
            <template #item="{ element: step, index: idx }">
              <div>
                <!-- linear send / wait row -->
                <div v-if="step.type !== 'branch'" class="step-row">
                  <span class="step-drag" title="Drag to reorder"><CygnusIcon name="more" /></span>
                  <span class="step-row__num">{{ idx + 1 }}</span>
                  <select v-model="step.type" class="form-select form-select-sm w-auto">
                    <option value="send_email">Send email</option>
                    <option v-if="directMail || step.type === 'send_mail'" value="send_mail">Send mail piece</option>
                    <option value="wait">Wait</option>
                  </select>
                  <template v-if="step.type === 'send_email'">
                    <MusubiEntitySelect
                      v-model="step.template_id"
                      endpoint="/api/v1/branding/templates" response-key="templates"
                      :option-label="t => t.title" :filter="t => t.surface === 'email'"
                      placeholder="Select email template…" create-label="+ Create new template"
                      style="max-width:230px" @create="openTemplateCreate" />
                    <input v-model="step.subject_override" type="text" class="form-control form-control-sm" placeholder="Subject override (optional)">
                  </template>
                  <template v-else-if="step.type === 'send_mail'">
                    <!-- B1/B2 — pick a READY mail-piece design; the runner accumulates
                         one physical piece per recipient into a daily posted batch. -->
                    <MusubiEntitySelect
                      v-if="directMail"
                      v-model="step.design_id"
                      endpoint="/api/v1/direct-mail/designs" response-key="designs"
                      :option-label="d => d.name" :filter="d => d.status === 'ready'"
                      placeholder="Select mail-piece design…" style="max-width:230px" />
                    <span v-else class="text-muted small">Direct Mail is not switched on for this workspace, so this flow cannot be activated.</span>
                    <input v-model.number="step.monthly_cap" type="number" min="1" class="form-control form-control-sm" placeholder="Monthly cap (200)" style="max-width:150px" title="Max pieces this flow posts per month (blank = 200)">
                  </template>
                  <template v-else>
                    <input v-model.number="step.wait_value" type="number" min="1" class="form-control form-control-sm" placeholder="Duration" style="max-width:110px">
                    <select v-model.number="step.wait_unit" class="form-select form-select-sm w-auto">
                      <option :value="60">{{ unitLabel(step.wait_value, 'minute') }}</option>
                      <option :value="3600">{{ unitLabel(step.wait_value, 'hour') }}</option>
                      <option :value="86400">{{ unitLabel(step.wait_value, 'day') }}</option>
                    </select>
                  </template>
                  <div class="step-row__actions btn-group btn-group-sm ms-auto">
                    <button
                      v-if="step.type === 'send_email'"
                      type="button"
                      class="btn"
                      :class="step.send_window ? 'btn-info text-white' : 'btn-outline-secondary'"
                      title="Per-step send window"
                      @click="toggleStepWin(step)"
                    ><CygnusIcon name="clock" /></button>
                    <button type="button" class="btn btn-outline-danger" @click="removeStep(idx)"><CygnusIcon name="trash" /></button>
                  </div>
                </div>
                <div v-if="step.type === 'send_email' && step._win" class="step-win">
                  <SendWindowEditor
                    v-model="step.send_window"
                    compact
                    label="Override this step's send window"
                    :inherited-label="editor.send_window ? 'flow window' : 'workspace window'"
                    :inherited-summary="stepInheritSummary"
                    :default-timezone="workspaceTimezone"
                  />
                </div>

                <!-- branch node: split on engagement with the previous email.
                     Gate explicitly on step.type — never `v-else`: the send-window
                     `v-if` above (W1) sits between this and the linear-row `v-if`,
                     so a `v-else` here would bind to the send-window condition and
                     render the branch card for ordinary linear steps (whose yes/no
                     arms are undefined → crash in the arm list below). -->
                <div v-if="step.type === 'branch'" class="branch-card">
                  <div class="branch-card__head">
                    <span class="step-drag" title="Drag to reorder"><CygnusIcon name="more" /></span>
                    <span class="step-row__num">{{ idx + 1 }}</span>
                    <CygnusIcon name="code" class="text-info" />
                    <strong class="small">Branch</strong>
                    <span class="small text-muted">- if recipient</span>
                    <select v-model="step.signal" class="form-select form-select-sm w-auto" @change="onBranchSignalChange(step)">
                      <option value="opened">opened</option>
                      <option value="clicked">clicked</option>
                      <option v-if="hasSendMailStep" value="scanned_mail">scanned the mail piece</option>
                    </select>
                    <span class="small text-muted">{{ step.signal === 'scanned_mail' ? 'within' : 'the previous email within' }}</span>
                    <input v-model.number="step.wait_value" type="number" min="1" class="form-control form-control-sm" style="max-width:80px">
                    <select v-model.number="step.wait_unit" class="form-select form-select-sm w-auto">
                      <option v-if="step.signal !== 'scanned_mail'" :value="3600">{{ unitLabel(step.wait_value, 'hour') }}</option>
                      <option :value="86400">{{ unitLabel(step.wait_value, 'day') }}</option>
                    </select>
                    <button type="button" class="btn btn-outline-danger btn-sm ms-auto" @click="removeStep(idx)"><CygnusIcon name="trash" /></button>
                  </div>
                  <div class="branch-card__arms">
                    <div v-for="arm in ['yes','no']" :key="arm" class="branch-arm" :class="`branch-arm--${arm}`">
                      <div class="branch-arm__label">
                        {{ arm === 'yes' ? `✓ Yes - they ${signalVerb(step.signal)}` : '✗ No - they didn’t' }}
                      </div>
                      <draggable
                        v-model="step[arm]"
                        item-key="_k"
                        handle=".step-drag"
                        :group="{ name: 'dripSteps' }"
                        :move="onStepMove"
                        class="branch-arm__list"
                        ghost-class="step-ghost"
                        tag="div"
                      >
                        <template #item="{ element: as, index: ai }">
                          <div>
                            <div class="step-row step-row--arm">
                              <span class="step-drag" title="Drag to reorder / between arms"><CygnusIcon name="more" /></span>
                              <select v-model="as.type" class="form-select form-select-sm w-auto">
                                <option value="send_email">Send email</option>
                                <option value="wait">Wait</option>
                              </select>
                              <template v-if="as.type === 'send_email'">
                                <MusubiEntitySelect
                                  v-model="as.template_id"
                                  endpoint="/api/v1/branding/templates" response-key="templates"
                                  :option-label="t => t.title" :filter="t => t.surface === 'email'"
                                  placeholder="Select template…" create-label="+ Create new template"
                                  style="max-width:200px" @create="openTemplateCreate" />
                                <input v-model="as.subject_override" type="text" class="form-control form-control-sm" placeholder="Subject (optional)">
                              </template>
                              <template v-else>
                                <input v-model.number="as.wait_value" type="number" min="1" class="form-control form-control-sm" placeholder="Duration" style="max-width:100px">
                                <select v-model.number="as.wait_unit" class="form-select form-select-sm w-auto">
                                  <option :value="60">{{ unitLabel(as.wait_value, 'minute') }}</option>
                                  <option :value="3600">{{ unitLabel(as.wait_value, 'hour') }}</option>
                                  <option :value="86400">{{ unitLabel(as.wait_value, 'day') }}</option>
                                </select>
                              </template>
                              <div class="btn-group btn-group-sm ms-auto">
                                <button
                                  v-if="as.type === 'send_email'"
                                  type="button"
                                  class="btn"
                                  :class="as.send_window ? 'btn-info text-white' : 'btn-outline-secondary'"
                                  title="Per-step send window"
                                  @click="toggleStepWin(as)"
                                ><CygnusIcon name="clock" /></button>
                                <button type="button" class="btn btn-outline-danger" @click="step[arm].splice(ai, 1)"><CygnusIcon name="trash" /></button>
                              </div>
                            </div>
                            <div v-if="as.type === 'send_email' && as._win" class="step-win">
                              <SendWindowEditor
                                v-model="as.send_window"
                                compact
                                label="Override this step's send window"
                                :inherited-label="editor.send_window ? 'flow window' : 'workspace window'"
                                :inherited-summary="stepInheritSummary"
                                :default-timezone="workspaceTimezone"
                              />
                            </div>
                          </div>
                        </template>
                      </draggable>
                      <div v-if="(step[arm] || []).length === 0" class="text-muted small fst-italic mb-1">No steps — drag one here, or the flow ends.</div>
                      <div class="mt-1">
                        <button type="button" class="btn btn-sm btn-outline-secondary py-0 px-2" @click="addArmStep(step, arm, 'send_email')"><CygnusIcon name="envelope" class="me-1" />send</button>
                        <button type="button" class="btn btn-sm btn-outline-secondary py-0 px-2 ms-1" @click="addArmStep(step, arm, 'wait')"><CygnusIcon name="clock" class="me-1" />wait</button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </draggable>

          <div class="d-flex gap-2 mt-2 mb-3">
            <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="hasBranch" @click="addStep('send_email')"><CygnusIcon name="envelope" class="me-1" />Add send</button>
            <button v-if="directMail" type="button" class="btn btn-sm btn-outline-secondary" :disabled="hasBranch" @click="addStep('send_mail')"><CygnusIcon name="envelope-open-text" class="me-1" />Add mail piece</button>
            <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="hasBranch" @click="addStep('wait')"><CygnusIcon name="clock" class="me-1" />Add wait</button>
            <button type="button" class="btn btn-sm btn-outline-info" :disabled="hasBranch || !hasSendBeforeEnd" @click="addBranch" title="Split the flow on whether the recipient engaged with the previous email">
              <CygnusIcon name="code" class="me-1" />Add branch
            </button>
          </div>
          <p v-if="hasBranch" class="text-muted small fst-italic mb-3">
            A branch is the final step — each arm runs to its own end. (Rejoining paths &amp; multiple branches come later.)
          </p>

          <div class="d-flex gap-2">
            <button type="submit" class="btn btn-primary btn-sm" :disabled="busy.save || !editor.name">
              <CygnusIcon name="spinner" :spin="true" v-if="busy.save" />
              <span v-else>{{ editor.id ? 'Save changes' : 'Create flow' }}</span>
            </button>
            <template v-if="editor.id">
              <!-- Discovering: go live (via the confirm panel) or pause. -->
              <template v-if="editor.status === 'discovering'">
                <button type="button" class="btn btn-success btn-sm" @click="openGoLive">
                  <CygnusIcon name="rocket" class="me-1" />Go live
                </button>
                <button type="button" class="btn btn-warning btn-sm" @click="setStatus('pause')">
                  <CygnusIcon name="pause" class="me-1" />Pause
                </button>
              </template>
              <template v-else-if="editor.status !== 'active'">
                <button type="button" class="btn btn-success btn-sm" @click="setStatus('activate')">
                  <CygnusIcon name="play" class="me-1" />Activate
                </button>
                <!-- Opt into "gather without sending" — only meaningful with audience filters. -->
                <button type="button" class="btn btn-outline-info btn-sm" @click="setStatus('discover')">
                  <CygnusIcon name="search" class="me-1" />Switch to discovery
                </button>
              </template>
              <button v-else type="button" class="btn btn-warning btn-sm" @click="setStatus('pause')">
                <CygnusIcon name="pause" class="me-1" />Pause
              </button>
              <button type="button" class="btn btn-outline-danger btn-sm ms-auto" :disabled="editor.status === 'active'" @click="deleteFlow(editor.id)">
                <CygnusIcon name="trash" class="me-1" />Delete
              </button>
            </template>
          </div>

          <!-- Subscription stats + manual enroll (existing flow only) -->
          <div v-if="editor.id && editor.stats" class="mt-3 pt-3 border-top">
            <h6 v-if="editor.engagement" class="text-uppercase text-muted small fw-bold mb-2">Performance</h6>
            <div v-if="editor.engagement" class="stat-tiles mb-3">
              <div class="stat-tile stat-tile--wait"><div class="stat-tile__value">{{ editor.engagement.enrolled }}</div><div class="stat-tile__label">Added</div></div>
              <div class="stat-tile stat-tile--ok"><div class="stat-tile__value">{{ editor.engagement.sent }}</div><div class="stat-tile__label">Emails sent</div></div>
              <div class="stat-tile"><div class="stat-tile__value">{{ editor.engagement.opens }} <span class="stat-tile__pct" v-if="editor.engagement.sent">({{ pct(editor.engagement.opens, editor.engagement.sent) }}%)</span></div><div class="stat-tile__label">Opened</div></div>
              <div class="stat-tile"><div class="stat-tile__value">{{ editor.engagement.clicks }} <span class="stat-tile__pct" v-if="editor.engagement.sent">({{ pct(editor.engagement.clicks, editor.engagement.sent) }}%)</span></div><div class="stat-tile__label">Clicked</div></div>
            </div>
            <h6 class="text-uppercase text-muted small fw-bold mb-2">Subscriptions</h6>
            <div class="stat-tiles mb-2">
              <div v-for="b in subBuckets" :key="b.key" :class="['stat-tile', `stat-tile--${b.tone}`]">
                <div class="stat-tile__value">{{ editor.stats[b.key] ?? 0 }}</div>
                <div class="stat-tile__label">{{ b.label }}</div>
              </div>
            </div>
            <div v-if="editor.topLinks && editor.topLinks.length" class="mt-3">
              <h6 class="text-uppercase text-muted small fw-bold mb-2">Top clicked links</h6>
              <table class="table table-sm align-middle mb-0">
                <thead><tr><th>Link</th><th class="text-end">Clicks</th><th class="text-end">Unique</th></tr></thead>
                <tbody>
                  <tr v-for="l in editor.topLinks" :key="l.url">
                    <td class="text-truncate" style="max-width:380px"><a :href="safeHref(l.url)" target="_blank" rel="noopener">{{ l.url }}</a></td>
                    <td class="text-end">{{ l.clicks }}</td>
                    <td class="text-end">{{ l.unique_clicks }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="d-flex gap-2 align-items-center">
              <input v-model.number="enrollSubId" type="number" class="form-control form-control-sm" placeholder="Subscriber ID" style="max-width:160px">
              <button type="button" class="btn btn-sm btn-outline-primary" :disabled="!enrollSubId" @click="enroll">Enroll subscriber</button>
              <span v-if="lastEnroll" :class="['small', enrollOk ? 'text-success' : 'text-danger']">{{ lastEnroll }}</span>
            </div>
          </div>
        </form>

        <!-- Task #54 — trigger rules that enrol into THIS flow (existing flow only) -->
        <FlowTriggers
          v-if="editor.id"
          :flow-id="editor.id"
          :flow-status="editor.status"
          class="mt-3 pt-3 border-top"
        />
      </div>
    </section>

    <div v-if="busy.load" class="flow-grid__status text-muted small">
      <CygnusIcon name="spinner" :spin="true" class="me-1" />Loading flows…
    </div>
    <div v-else-if="!rows.length" class="flow-grid__status text-muted small fst-italic">
      No flows yet — click "New flow" above to create one.
    </div>
    <div v-else class="flow-grid">
      <div
        v-for="flow in rows"
        :key="flow.id"
        class="flow-card"
        role="button"
        tabindex="0"
        @click="onRowClick(flow)"
        @keydown.enter="onRowClick(flow)"
      >
        <div class="flow-card__row">
          <h4>{{ flow.name }}</h4>
          <span :class="['status-pill', `status-pill--${flow.status}`]">{{ flow.status }}</span>
        </div>
        <div class="flow-card__sub">{{ triggerLabel(flow.trigger_type) }}</div>

        <!-- Per-flow performance aggregates (enrolled/completed/open rate/replies)
             aren't in the list payload — index() only selects id/name/status/
             trigger_type/sender_identity_id/created (see AdminDripFlowsController::index).
             These stay as placeholders until a summary-stats endpoint exists;
             open a flow's editor for its real subscription/engagement numbers. -->
        <div class="flow-card__stats">
          <div class="flow-card__stat"><div class="v">—</div><div class="k">Enrolled</div></div>
          <div class="flow-card__stat"><div class="v">—</div><div class="k">Completed</div></div>
          <div class="flow-card__stat"><div class="v">—</div><div class="k">Open rate</div></div>
          <div class="flow-card__stat"><div class="v">—</div><div class="k">Replies</div></div>
        </div>

        <div class="flow-card__meta">
          <span>Created {{ formatDate(flow.created) }}</span>
          <span>Flow #{{ flow.id }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";
import draggable from "vuedraggable";
import MusubiEntitySelect from "@root/components/MusubiEntitySelect.vue";
import MusubiSectorSelect from "@root/components/MusubiSectorSelect.vue";
import CrawlSourceFields from "@root/components/CrawlSourceFields.vue";
import SendWindowEditor from "@root/components/SendWindowEditor.vue";
import SendTimingToggle from "@root/components/SendTimingToggle.vue";
import FlowTriggers from "@root/components/FlowTriggers.vue";
import TriggerReviewQueue from "@root/components/TriggerReviewQueue.vue";
import { SOURCE_TYPES } from "./util/crawlSourceForm";
import { safeUrl } from "./util/cellHtml";
import { summariseWindow } from "./util/sendWindowSummary";

// Stable per-step keys for vuedraggable's item-key (the editor step objects
// have no id of their own). UI-only — never serialised to the API.
let _stepKeySeq = 0;
const nextStepKey = () => `s${++_stepKeySeq}`;

export default {
  name: 'AdminDripFlows',
  components: { draggable, MusubiEntitySelect, MusubiSectorSelect, CrawlSourceFields, SendWindowEditor, SendTimingToggle, FlowTriggers, TriggerReviewQueue },
  props: { componentData: { type: Object, default: () => ({}) } },
  data() {
    return {
      api: useApi(),
      rows: [],
      editor: null,
      // Whether this workspace has Direct Mail (the index response says so);
      // the Send mail piece step is offered only then.
      directMail: false,
      busy: { load: false, save: false, tick: false, sync: false, preview: false, golive: false },
      // DISCOVERY MODE — building counts + go-live confirm state for the open flow.
      discovery: null,
      goLiveOpen: false,
      frameworks: [],
      regions: [],
      audiencePreview: null,
      error: null,
      lastTick: '',
      enrollSubId: null,
      lastEnroll: '',
      enrollOk: false,
      // crawl-source linkage (deliverable B): swap / edit the crawler behind a pipeline
      sources: [],
      sourcesForbidden: false,
      sourceEditOpen: false,
      sourceDraft: { type: '', name: '', config: {}, valid: false },
      savingSource: false,
      sourceMsg: '',
      // Set when "+ Create new template" opens the template editor in a new tab.
      templateHint: '',
      sourceMsgOk: false,
      pendingFlowId: null,   // ?flow={id} deep-link, opened after refresh
      // W1 — workspace send window (inherit hint + editor default tz).
      workspaceWindowSummary: '',
      workspaceTimezone: 'Europe/London',
      // W2 — send-time optimisation entitlement (send-settings).
      stoEntitled: false,
    };
  },
  computed: {
    // W1 — what a per-step override inherits when off: the flow window if the
    // flow overrides, else the workspace window.
    stepInheritSummary() {
      if (this.editor?.send_window) return summariseWindow(this.editor.send_window);
      return this.workspaceWindowSummary;
    },
    subBuckets() {
      return [
        { key: 'active',    label: 'Active',    tone: 'wait' },
        { key: 'completed', label: 'Completed', tone: 'ok' },
        { key: 'paused',    label: 'Paused',    tone: 'warn' },
        { key: 'exited',    label: 'Exited',    tone: 'error' },
      ];
    },
    // Branching: a branch is always the final top-level step (P1 — terminal arms).
    branchIndex() { return (this.editor?.steps || []).findIndex(s => s.type === 'branch'); },
    hasBranch() { return this.branchIndex !== -1; },
    hasSendBeforeEnd() {
      const steps = this.editor?.steps || [];
      const end = this.hasBranch ? this.branchIndex : steps.length;
      // A branch needs something measurable before it: an email (opened/clicked)
      // OR a posted piece (scanned_mail). Either qualifies to enable branching.
      return steps.slice(0, end).some(s => s.type === 'send_email' || s.type === 'send_mail');
    },
    // B3 — a flow that posts a physical piece. Only then is the scan branch
    // meaningful, so the picker offers "scanned the mail piece" only here.
    hasSendMailStep() { return (this.editor?.steps || []).some(s => s.type === 'send_mail'); },
    linkedSource() {
      if (!this.editor || !this.editor.source_id) return null;
      return this.sources.find(s => s.id === this.editor.source_id) || null;
    },
    /**
     * Live sources, plus whichever one this flow is ALREADY linked to even if it
     * has since been archived. Archived sources should not be offered as a new
     * choice, but silently dropping the flow's existing link from the dropdown
     * would make the field read "no source linked" and let a save wipe a real
     * link the operator never touched.
     */
    pickableSources() {
      const linkedId = this.editor && this.editor.source_id;
      return this.sources.filter((s) => !s.archived_at || s.id === linkedId);
    },
  },
  mounted() {
    // Deep-link: /outreach/drip-flows?flow={id} opens that flow's editor directly
    // (from PipelinesHome "Edit targets"). Parsed before refresh; opened after.
    try {
      const q = new URLSearchParams(window.location.search).get('flow');
      const id = q ? Number(q) : null;
      if (id && Number.isFinite(id)) this.pendingFlowId = id;
    } catch (e) { /* no query — start on the list */ }
    this.refresh();
    this.loadSources();
    this.loadWorkspaceWindow();
    this.loadDistinct('website_framework', 'frameworks');
    this.loadDistinct('region', 'regions');
  },
  methods: {
    // Task #54 — a review-queue approve/dismiss may have enrolled a prospect;
    // refresh the flow cards so their Added counts reflect it.
    onQueueChanged() {
      this.refresh();
    },
    // W1 — fetch the workspace send window for the inherit hint + default tz.
    async loadWorkspaceWindow() {
      try {
        const res = await this.api.get('/api/v1/admin/email/send-settings');
        const settings = (res?.data ?? res)?.settings || {};
        const sw = settings.send_window || {};
        this.stoEntitled = !!(settings.send_timing && settings.send_timing.entitled);
        if (sw.timezone) this.workspaceTimezone = sw.timezone;
        this.workspaceWindowSummary = (sw.enabled && Array.isArray(sw.days) && sw.days.length)
          ? summariseWindow({ days: sw.days, ranges: [{ start: (sw.start_hour || 0) * 60, end: (sw.end_hour || 0) * 60 }], timezone: sw.timezone || this.workspaceTimezone })
          : '';
      } catch (e) { /* non-fatal */ }
    },
    // Toggle the inline per-step window editor (UI-only flag, never serialised).
    toggleStepWin(step) { step._win = !step._win; },
    // Sanitise a server-derived tracked-link URL before it lands in an href —
    // Vue attribute-escapes but does not neutralise javascript:/data: schemes.
    safeHref(url) { return safeUrl(url); },
    // Flow-card subline — trigger_type is one of the values index() actually
    // returns; keep this in sync with SUPPORTED_TRIGGERS in AdminDripFlowsController.
    triggerLabel(type) {
      return ({
        manual:      'Manual enrollment',
        list_join:   'Enrols on list join',
        form_submit: 'Enrols on form submit',
        site_visit:  'Enrols on website visit',
        event:       'Event trigger (no dispatch yet)',
      })[type] || type || 'Manual enrollment';
    },
    formatDate(secs) { return secs ? new Date(secs * 1000).toLocaleDateString() : '—'; },
    // "1 minutes" reads as broken — singularise the unit option label when
    // the paired duration input is exactly 1.
    unitLabel(qty, singular) { return Number(qty) === 1 ? singular : `${singular}s`; },
    async refresh() {
      this.busy.load = true; this.error = null;
      try {
        const res = await this.api.get('/api/v1/admin/drip-flows');
        const payload = res?.data?.data ?? res?.data ?? res;
        this.rows = Array.isArray(payload) ? payload : (payload?.rows || []);
        this.directMail = (res?.direct_mail ?? res?.data?.direct_mail) === true;
        // Open a ?flow={id} deep-link once the list is in.
        if (this.pendingFlowId && this.rows.some(r => r.id === this.pendingFlowId)) {
          const id = this.pendingFlowId;
          this.pendingFlowId = null;
          this.onRowClick({ id });
        }
      } catch (e) { this.error = e?.message || 'Failed to load flows.'; }
      this.busy.load = false;
    },
    async loadSources() {
      try {
        // ?archived=1 so a flow linked to an archived source can still resolve
        // its name. pickableSources() is what filters the dropdown.
        const res = await this.api.get('/api/v1/admin/crawl/sources?archived=1');
        const p = res?.data?.data ?? res?.data ?? res;
        const list = Array.isArray(p) ? p : (p?.sources || p?.rows || p?.data || []);
        this.sources = Array.isArray(list) ? list : [];
      } catch (e) {
        // Crawl sources are Ops::ACQUIRE — a pure OUTREACH role 403s here.
        if (e?.response?.status === 403) this.sourcesForbidden = true;
      }
    },
    sourceTypeLabel(type) {
      return (SOURCE_TYPES.find(t => t.value === type) || {}).label || type || '—';
    },
    /**
     * "+ Create new template" in a step's template picker.
     *
     * A new tab, not a route push. This editor keeps the whole flow in plain
     * component state - no autosave, no store, no beforeRouteLeave - so
     * navigating away in this tab would silently bin an unsaved flow. The
     * wizard already settled on the same answer for the same reason
     * (PipelineWizard's "no route change, so the wizard state survives").
     * ?new=1 opens the create form directly instead of dropping the operator on
     * a template list they then have to work out.
     */
    openTemplateCreate() {
      window.open('/admin/email-templates?new=1', '_blank', 'noopener');
      this.templateHint = 'Creating a template in a new tab. Come back and reopen this dropdown to pick it.';
    },
    toggleSourceEdit() { this.sourceEditOpen = !this.sourceEditOpen; this.sourceMsg = ''; },
    closeSourceEdit() { this.sourceEditOpen = false; this.sourceMsg = ''; },
    onSourceFieldsChange(payload) { this.sourceDraft = payload; },
    async saveSourceParams() {
      if (!this.linkedSource || !this.sourceDraft.valid) return;
      this.savingSource = true; this.sourceMsg = '';
      try {
        await this.api.patch(`/api/v1/admin/crawl/sources/${this.linkedSource.id}`, {
          name: this.sourceDraft.name,
          config: this.sourceDraft.config,
        });
        this.sourceMsg = 'Source updated.'; this.sourceMsgOk = true;
        await this.loadSources();
      } catch (e) {
        this.sourceMsg = e?.response?.data?.error || e?.message || 'Could not update the source.'; this.sourceMsgOk = false;
      }
      this.savingSource = false;
    },
    openCreate() {
      this.editor = {
        name: '', description: '', trigger_type: 'manual',
        sv_min_confidence: 'high', sv_auto_enroll: false, sv_sectors: [], sv_region: '',
        sender_identity_id: null, trigger_list_id: null, source_id: null, audience: [], steps: [], stats: null, engagement: null,
        send_window: null,
        send_timing: 'window',
      };
      this.lastEnroll = '';
      this.audiencePreview = null;
      this.sourceEditOpen = false;
    },
    async loadDistinct(field, into) {
      try {
        const res = await this.api.get(`/api/v1/admin/leads/distinct/${field}`);
        this[into] = (res?.data ?? res)?.values || [];
      } catch (e) { /* non-fatal — typeahead just won't suggest */ }
    },
    pct(n, d) { return d > 0 ? Math.round((n / d) * 100) : 0; },
    async previewAudience() {
      this.busy.preview = true;
      try {
        const res = await this.api.post('/api/v1/admin/drip-flows/audiences/preview', {
          audience_json: this.audienceToApi(),
          flow_id: this.editor.id || 0,
        });
        this.audiencePreview = res?.data ?? res;
      } catch (e) { this.audiencePreview = null; }
      this.busy.preview = false;
    },
    addAudienceSource() {
      this.editor.audience.push({
        temperature: '', stage: '', framework: '', region: '',
        sectors: [], company_status: '', min_employees: 0, min_score: 0, leads_only: false,
      });
    },
    // API source → editor row (fill in the firmographic keys so their v-model
    // bindings are reactive even for flows saved before those fields existed).
    audienceFromApi(s) {
      s = s || {};
      return {
        temperature: s.temperature || '',
        stage: s.stage || '',
        framework: s.framework || '',
        region: s.region || '',
        sectors: Array.isArray(s.sectors) ? s.sectors : (s.sectors ? [s.sectors] : []),
        company_status: Array.isArray(s.company_status) ? (s.company_status[0] || '') : (s.company_status || ''),
        min_employees: Number(s.min_employees) || 0,
        min_score: Number(s.min_score) || 0,
        leads_only: !!s.leads_only,
      };
    },
    // editor audience rows → clean API sources (drop blanks)
    audienceToApi() {
      return (this.editor.audience || []).map((s) => {
        const o = {};
        if (s.temperature) o.temperature = s.temperature;
        if (s.stage) o.stage = s.stage;
        if (s.framework && String(s.framework).trim()) o.framework = String(s.framework).trim();
        if (s.region && String(s.region).trim()) o.region = String(s.region).trim();
        if (Array.isArray(s.sectors) && s.sectors.length) o.sectors = s.sectors;
        if (s.company_status) o.company_status = s.company_status;
        if (Number(s.min_employees) > 0) o.min_employees = Number(s.min_employees);
        if (Number(s.min_score) > 0) o.min_score = Number(s.min_score);
        if (s.leads_only) o.leads_only = true;
        return o;
      });
    },
    closeEditor() { this.editor = null; },
    newLinear(type) {
      if (type === 'send_email') {
        return { _k: nextStepKey(), type, template_id: null, subject_override: '', send_window: null, _win: false };
      }
      // B1/B2 — a "post a physical piece" step: pins a ready mail-piece design
      // and an optional per-step monthly cap (blank → the flow default of 200).
      if (type === 'send_mail') {
        return { _k: nextStepKey(), type, design_id: null, monthly_cap: null };
      }
      return { _k: nextStepKey(), type: 'wait', wait_value: 1, wait_unit: 86400 };
    },
    addStep(type) { this.editor.steps.push(this.newLinear(type)); },
    addBranch() {
      // B3 — in a mail-posting flow the natural branch is "did they scan?", on a
      // 14-day wait-for-scan window; otherwise the email engagement branch (2 days).
      const scan = this.hasSendMailStep;
      this.editor.steps.push({
        _k: nextStepKey(), type: 'branch',
        signal: scan ? 'scanned_mail' : 'opened',
        wait_value: scan ? 14 : 2, wait_unit: 86400,
        yes: [], no: [],
      });
    },
    // Friendly past-tense verb for a branch arm label.
    signalVerb(signal) {
      if (signal === 'scanned_mail') return 'scanned the mail';
      if (signal === 'clicked') return 'clicked';
      return 'opened';
    },
    // Switching to the scan signal: its window is measured in DAYS only (a posted
    // piece takes days to arrive), so snap an hours unit to days and default the
    // window to 14 when it was left at the small email default.
    onBranchSignalChange(step) {
      if (step.signal === 'scanned_mail') {
        if (Number(step.wait_unit) !== 86400) {
          step.wait_unit = 86400;
          if (!(Number(step.wait_value) > 0)) step.wait_value = 14;
        }
      }
    },
    // vuedraggable move guard. The branch is always terminal (P1 — each arm
    // runs to its own end). The branch CARD is draggable (for a consistent
    // affordance) but only within the top-level list — never into an arm —
    // and `normalizeTop` snaps it back to last after any drop. Linear steps
    // may not land after the branch in the top list. Cross-list moves into/out
    // of the Yes/No arms are otherwise free (shared group).
    onStepMove(evt) {
      const dragged = evt.draggedContext?.element;
      const toList = evt.relatedContext?.list;
      if (dragged?.type === 'branch') return toList === this.editor.steps;
      if (toList === this.editor.steps) {
        const bIdx = this.editor.steps.findIndex((s) => s.type === 'branch');
        if (bIdx !== -1 && evt.draggedContext.futureIndex > bIdx) return false;
      }
      return true;
    },
    // After any top-level drag, keep the branch as the final step (the only
    // valid place for it) — guards against a branch dragged above linear steps.
    normalizeTop() {
      const steps = this.editor.steps;
      const bIdx = steps.findIndex((s) => s.type === 'branch');
      if (bIdx !== -1 && bIdx !== steps.length - 1) {
        const [branch] = steps.splice(bIdx, 1);
        steps.push(branch);
      }
    },
    removeStep(i) { this.editor.steps.splice(i, 1); },
    addArmStep(branch, arm, type) { branch[arm].push(this.newLinear(type)); },

    secondsToWait(secs) {
      secs = Number(secs) || 0;
      if (secs > 0 && secs % 86400 === 0) return { wait_value: secs / 86400, wait_unit: 86400 };
      if (secs > 0 && secs % 3600 === 0) return { wait_value: secs / 3600, wait_unit: 3600 };
      return { wait_value: Math.max(1, Math.round(secs / 60)), wait_unit: 60 };
    },
    waitSeconds(s) { return (Number(s.wait_value) || 0) * (Number(s.wait_unit) || 1); },

    // ── editor ⇄ steps_json ──────────────────────────────────────────────
    // A linear (send/wait) editor step → an API node. `id`/`next` only emitted
    // for branching flows (node shape); a plain linear flow stays id-less so it
    // keeps using the runner's legacy index path.
    nodeOut(s, id, next) {
      if (s.type === 'wait') return { id, type: 'wait', duration_seconds: this.waitSeconds(s), next };
      if (s.type === 'send_mail') {
        const o = { id, type: 'send_mail', design_id: Number(s.design_id) || 0, next };
        if (Number(s.monthly_cap) > 0) o.monthly_cap = Number(s.monthly_cap);
        return o;
      }
      const o = { id, type: 'send_email', template_id: Number(s.template_id) || 0, next };
      if (s.subject_override) o.subject_override = s.subject_override;
      if (s.send_window) o.send_window = s.send_window; // W1 per-step override
      return o;
    },
    legacyOut(s) {
      if (s.type === 'wait') return { type: 'wait', duration_seconds: this.waitSeconds(s) };
      if (s.type === 'send_mail') {
        const o = { type: 'send_mail', design_id: Number(s.design_id) || 0 };
        if (Number(s.monthly_cap) > 0) o.monthly_cap = Number(s.monthly_cap);
        return o;
      }
      const o = { type: 'send_email', template_id: Number(s.template_id) || 0 };
      if (s.subject_override) o.subject_override = s.subject_override;
      if (s.send_window) o.send_window = s.send_window; // W1 per-step override
      return o;
    },
    linearFromNode(s) {
      if (s.type === 'wait') return { _k: nextStepKey(), type: 'wait', ...this.secondsToWait(s.duration_seconds) };
      if (s.type === 'send_mail') {
        return { _k: nextStepKey(), type: 'send_mail', design_id: s.design_id ?? null, monthly_cap: s.monthly_cap ?? null };
      }
      const sw = s.send_window && Array.isArray(s.send_window.days) && s.send_window.days.length ? s.send_window : null;
      return { _k: nextStepKey(), type: 'send_email', template_id: s.template_id ?? null, subject_override: s.subject_override ?? '', send_window: sw, _win: !!sw };
    },

    // editor steps → API steps_json
    stepsToApi() {
      const top = this.editor.steps;
      const bIdx = top.findIndex(s => s.type === 'branch');
      if (bIdx === -1) return top.map(s => this.legacyOut(s));

      const branch = top[bIdx];
      const prefix = top.slice(0, bIdx).filter(s => s.type !== 'branch');
      let c = 0; const mint = (p) => `${p}${c++}`;
      const pre = prefix.map(s => ({ raw: s, id: mint('n') }));
      const branchId = mint('b');
      const yes = (branch.yes || []).map(s => ({ raw: s, id: mint('y') }));
      const no  = (branch.no  || []).map(s => ({ raw: s, id: mint('d') }));

      const signal = branch.signal || 'opened';
      const refType = signal === 'scanned_mail' ? 'send_mail' : 'send_email';
      let ref = '';
      pre.forEach(p => { if (p.raw.type === refType) ref = p.id; }); // nearest preceding matching send

      const nodes = [];
      pre.forEach((p, i) => nodes.push(this.nodeOut(p.raw, p.id, i < pre.length - 1 ? pre[i + 1].id : branchId)));
      const branchNode = {
        id: branchId, type: 'branch',
        condition: { signal, ref_step: ref },
        on_true: yes[0]?.id ?? null,
        on_false: no[0]?.id ?? null,
      };
      // B3 — the scan branch expresses its wait-for-scan window in DAYS
      // (window_days); the engagement branches keep wait_seconds.
      if (signal === 'scanned_mail') {
        branchNode.window_days = Math.max(1, Math.round(this.waitSeconds(branch) / 86400));
      } else {
        branchNode.wait_seconds = this.waitSeconds(branch);
      }
      nodes.push(branchNode);
      yes.forEach((y, i) => nodes.push(this.nodeOut(y.raw, y.id, yes[i + 1]?.id ?? null)));
      no.forEach((d, i) => nodes.push(this.nodeOut(d.raw, d.id, no[i + 1]?.id ?? null)));
      return nodes;
    },

    // API steps_json → editor steps
    stepsFromApi(steps) {
      const arr = Array.isArray(steps) ? steps : [];
      const isNodeShape = arr.some(s => s && (s.id !== undefined || s.type === 'branch'));
      if (!isNodeShape) return arr.map(s => this.linearFromNode(s));

      const byId = {}; arr.forEach(s => { byId[String(s.id)] = s; });
      const referenced = new Set();
      arr.forEach(s => {
        if (s.next != null) referenced.add(String(s.next));
        if (s.on_true != null) referenced.add(String(s.on_true));
        if (s.on_false != null) referenced.add(String(s.on_false));
      });
      const head = arr.find(s => !referenced.has(String(s.id))) || arr[0];
      const chain = (startId, stopId) => {
        const out = []; const seen = new Set();
        let cur = startId != null ? String(startId) : null;
        const stop = stopId != null ? String(stopId) : null;
        while (cur && byId[cur] && cur !== stop && !seen.has(cur)) {
          seen.add(cur);
          const n = byId[cur];
          if (n.type === 'branch') break;
          out.push(this.linearFromNode(n));
          cur = n.next != null ? String(n.next) : null;
        }
        return out;
      };
      const branch = arr.find(s => s.type === 'branch');
      if (!branch) return chain(head?.id, null);

      const signal = branch.condition?.signal || 'opened';
      // B3 — the scan branch stores its window as window_days (default 14);
      // engagement branches store wait_seconds.
      const w = signal === 'scanned_mail'
        ? { wait_value: Math.max(1, Number(branch.window_days) || 14), wait_unit: 86400 }
        : this.secondsToWait(branch.wait_seconds);
      return [
        ...chain(head?.id, branch.id),
        {
          _k: nextStepKey(),
          type: 'branch',
          signal,
          wait_value: w.wait_value, wait_unit: w.wait_unit,
          yes: chain(branch.on_true, null),
          no:  chain(branch.on_false, null),
        },
      ];
    },
    async onRowClick(row) {
      if (!row?.id) return;
      try {
        const res = await this.api.get(`/api/v1/admin/drip-flows/${row.id}`);
        const payload = res?.data ?? res;
        const f = payload?.flow;
        if (!f) return;
        this.editor = {
          id: f.id,
          name: f.name,
          description: f.description || '',
          status: f.status,
          trigger_type: f.trigger_type,
          trigger_list_id: f.trigger_config?.list_id ?? null,
          sv_min_confidence: f.trigger_config?.min_confidence === 'medium' ? 'medium' : 'high',
          sv_auto_enroll: !!f.trigger_config?.auto_enroll,
          sv_sectors: Array.isArray(f.trigger_config?.sectors) ? f.trigger_config.sectors : [],
          sv_region: Array.isArray(f.trigger_config?.regions) ? (f.trigger_config.regions[0] || '') : (f.trigger_config?.regions || ''),
          sender_identity_id: f.sender_identity_id ?? null,
          source_id: f.source_id ?? null,
          send_window: f.send_window || null,
          send_timing: f.send_timing === 'optimise' ? 'optimise' : 'window',
          audience: (Array.isArray(f.audience_json) ? f.audience_json : []).map((s) => this.audienceFromApi(s)),
          steps: this.stepsFromApi(f.steps_json),
          stats: payload.subscription_stats || {},
          engagement: payload.engagement || null,
          topLinks: payload.top_links || [],
        };
        this.lastEnroll = '';
        this.audiencePreview = null;
        this.sourceEditOpen = false;
        this.sourceMsg = '';
        // DISCOVERY MODE — a discovering flow shows building counts + a go-live
        // number; fetch them fresh whenever its detail opens.
        this.discovery = null;
        this.goLiveOpen = false;
        if (f.status === 'discovering') await this.loadDiscoveryCounts(f.id);
      } catch (e) { this.error = e?.message || 'Failed to open flow.'; }
    },
    // DISCOVERY MODE — request-time counts (discovered / enriched / would-enrol)
    // + the honest go-live sentence.
    async loadDiscoveryCounts(id) {
      try {
        const res = await this.api.get(`/api/v1/admin/drip-flows/${id}/discovery-counts`);
        this.discovery = res?.data ?? res;
      } catch (e) { this.discovery = null; }
    },
    async openGoLive() {
      // Refresh the number just before showing the confirm — it drifts as
      // discovery continues, so the operator sees the latest.
      if (this.editor?.id) await this.loadDiscoveryCounts(this.editor.id);
      this.goLiveOpen = true;
    },
    async confirmGoLive() {
      this.busy.golive = true;
      try {
        await this.api.post(`/api/v1/admin/drip-flows/${this.editor.id}/activate`, {});
        this.goLiveOpen = false;
        await this.refresh();
        await this.onRowClick({ id: this.editor.id });
      } catch (e) { this.error = e?.response?.data?.error || e?.message || 'Go-live failed.'; }
      this.busy.golive = false;
    },
    fmt(n) { return Number(n || 0).toLocaleString(); },
    async saveFlow() {
      if (this.hasBranch && !this.hasSendBeforeEnd) {
        this.error = 'A branch needs a Send step before it (so it has an email whose opens/clicks to measure).';
        return;
      }
      // B3 — a scan branch needs a posted piece to measure.
      const branch = (this.editor?.steps || []).find(s => s.type === 'branch');
      if (branch && branch.signal === 'scanned_mail' && !this.hasSendMailStep) {
        this.error = 'A scan branch needs a mail-piece step before it (so there is a posted piece whose scan to measure).';
        return;
      }
      this.busy.save = true; this.error = null;
      const body = {
        name: this.editor.name,
        description: this.editor.description || null,
        trigger_type: this.editor.trigger_type,
        sender_identity_id: this.editor.sender_identity_id || null,
        source_id: this.editor.source_id || null,
        audience_json: this.audienceToApi(),
        steps_json: this.stepsToApi(),
        send_window_json: this.editor.send_window || null, // W1 flow-level (null = inherit/clear)
        send_timing: this.editor.send_timing === 'optimise' ? 'optimise' : 'window', // W2 mode
      };
      if (this.editor.trigger_type === 'list_join' && this.editor.trigger_list_id) {
        body.trigger_config = { list_id: Number(this.editor.trigger_list_id) };
      }
      if (this.editor.trigger_type === 'site_visit') {
        const region = String(this.editor.sv_region || '').trim();
        body.trigger_config = {
          min_confidence: this.editor.sv_min_confidence === 'medium' ? 'medium' : 'high',
          auto_enroll: !!this.editor.sv_auto_enroll,
          sectors: Array.isArray(this.editor.sv_sectors) ? this.editor.sv_sectors : [],
          regions: region ? [region] : [],
        };
      }
      try {
        if (this.editor.id) {
          await this.api.put(`/api/v1/admin/drip-flows/${this.editor.id}`, body);
        } else {
          const res = await this.api.post('/api/v1/admin/drip-flows', body);
          const payload = res?.data ?? res;
          if (payload?.flow?.id) this.editor.id = payload.flow.id;
          else { this.error = payload?.error || 'Create failed.'; this.busy.save = false; return; }
        }
        await this.refresh();
        await this.onRowClick({ id: this.editor.id });
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Save failed.';
      }
      this.busy.save = false;
    },
    async setStatus(action) {
      try {
        await this.api.post(`/api/v1/admin/drip-flows/${this.editor.id}/${action}`, {});
        await this.refresh();
        await this.onRowClick({ id: this.editor.id });
      } catch (e) { this.error = e?.response?.data?.error || e?.message || `${action} failed.`; }
    },
    async deleteFlow(id) {
      if (!confirm('Delete this flow? (Pause it first if active.)')) return;
      try {
        await this.api.delete(`/api/v1/admin/drip-flows/${id}`);
        this.editor = null;
        await this.refresh();
      } catch (e) { this.error = e?.response?.data?.error || e?.message || 'Delete failed.'; }
    },
    async enroll() {
      this.lastEnroll = ''; this.enrollOk = false;
      try {
        const res = await this.api.post(`/api/v1/admin/drip-flows/${this.editor.id}/enroll`, { subscriber_id: this.enrollSubId });
        const payload = res?.data ?? res;
        this.enrollOk = !!payload?.success;
        this.lastEnroll = payload?.success ? `Enrolled (subscription #${payload.subscription_id}).` : (payload?.reason || 'Enroll failed.');
        await this.onRowClick({ id: this.editor.id });
      } catch (e) { this.lastEnroll = e?.response?.data?.error || e?.message || 'Enroll failed.'; }
    },
    async tick() {
      this.busy.tick = true; this.lastTick = '';
      try {
        const res = await this.api.post('/api/v1/admin/drip-flows/runner/tick', {});
        const p = res?.data ?? res;
        this.lastTick = `Scanned ${p.scanned}, advanced ${p.advanced}, completed ${p.completed}, exited ${p.exited}.`;
        await this.refresh();
        if (this.editor?.id) await this.onRowClick({ id: this.editor.id });
      } catch (e) { this.lastTick = e?.response?.data?.error || e?.message || 'Tick failed.'; }
      this.busy.tick = false;
    },
    async syncAudiences() {
      this.busy.sync = true; this.lastTick = '';
      try {
        const res = await this.api.post('/api/v1/admin/drip-flows/audiences/sync', {});
        const p = res?.data ?? res;
        this.lastTick = `Audience sync: enrolled ${p.enrolled} prospect(s) across ${p.flows} active flow(s).`;
        await this.refresh();
        if (this.editor?.id) await this.onRowClick({ id: this.editor.id });
      } catch (e) { this.lastTick = e?.response?.data?.error || e?.message || 'Sync failed.'; }
      this.busy.sync = false;
    },
  },
};
</script>

<style>
.musubi-page--drips { padding: var(--m-space-6, 32px); }
.actions-bar { display: flex; align-items: center; gap: 10px; margin-bottom: 14px; }
.actions-bar__msg { font-size: 0.85rem; color: var(--m-text-secondary, #475569); }
.editor { background: var(--m-bg-card, #FFF); }
.step-row { display: flex; align-items: center; gap: 8px; padding: 6px 0; }
.step-win { margin: 2px 0 10px 30px; }

/* Audience (auto-enroll) */
.audience-box {
  border: 1px solid var(--m-border, #E2E8F0); border-radius: var(--m-radius, 8px);
  padding: 10px 12px; background: var(--m-bg-subtle, #F8FAFC);
}
.audience-source {
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  padding: 6px 0; border-top: 1px solid var(--m-border, #E2E8F0);
}
.audience-source:first-of-type { border-top: 0; }

/* Crawl-source linkage (edit / swap the crawler behind a pipeline) */
.source-box {
  border: 1px solid var(--m-border, #E2E8F0); border-radius: var(--m-radius, 8px);
  padding: 10px 12px; background: var(--m-bg-subtle, #F8FAFC);
}
.source-edit { border-top: 1px dashed var(--m-border, #E2E8F0); padding-top: 10px; }

/* Drag-and-drop step ordering (vuedraggable) */
.step-drag {
  cursor: grab; color: var(--m-text-tertiary, #9AA0AC); flex: 0 0 auto;
  padding: 2px 2px; border-radius: 4px; user-select: none;
}
.step-drag:hover { color: var(--m-accent, #4F46E5); background: var(--m-accent-soft, #EEF0FF); }
.step-drag:active { cursor: grabbing; }
.step-ghost { opacity: 0.5; background: var(--m-accent-soft, #EEF0FF); border-radius: var(--m-radius, 8px); }
/* Empty arm stays a visible drop target */
.branch-arm__list { min-height: 8px; }
.branch-arm__list:empty { min-height: 28px; border: 1px dashed var(--m-border-strong, #E0E3EC); border-radius: var(--m-radius, 8px); }
.step-row__num {
  display: inline-flex; align-items: center; justify-content: center;
  width: 22px; height: 22px; border-radius: 50%;
  background: var(--m-border, #E2E8F0); font-size: 0.7rem; font-weight: 600; flex: 0 0 auto;
}
.stat-tiles { display: grid; grid-template-columns: repeat(auto-fit, minmax(110px, 1fr)); gap: 8px; }
.stat-tile {
  background: var(--m-bg-card, #FFF); border: 1px solid var(--m-border, #E2E8F0);
  border-radius: var(--m-radius, 6px); padding: 10px 12px; border-left-width: 3px;
}
.stat-tile--wait  { border-left-color: var(--m-status-info, #3B82F6); }
.stat-tile--ok    { border-left-color: var(--m-status-success, #10B981); }
.stat-tile--warn  { border-left-color: var(--m-status-warning, #F59E0B); }
.stat-tile--error { border-left-color: var(--m-status-danger, #EF4444); }
.stat-tile__value { font-size: 1.15rem; font-weight: 600; line-height: 1; }
.stat-tile__pct { font-size: 0.72rem; color: var(--m-text-tertiary, #9AA0AC); font-weight: 400; }
.stat-tile__label { margin-top: 4px; font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--m-text-secondary, #475569); }
.status-pill {
  display: inline-block; padding: 2px 8px; font-size: 0.72rem; font-weight: 500;
  border-radius: 100px; text-transform: uppercase; letter-spacing: 0.03em; vertical-align: middle; margin-left: 6px;
}
.status-pill--draft  { background: #E5E7EB; color: #374151; }
.status-pill--active { background: #DCFCE7; color: #166534; }
.status-pill--paused { background: #FEF3C7; color: #92400E; }
/* Discovery — informational teal, deliberately NOT the paused grey/amber. */
.status-pill--discovering { background: #CCFBF1; color: #115E59; }

/* Discovery banner: "gathering, not sending yet" + building counts. */
.disc-banner {
  border: 1px solid #99F6E4; background: #F0FDFA; border-radius: var(--m-radius, 8px);
  padding: 12px 14px; margin-bottom: 14px;
}
.disc-banner__head { display: flex; align-items: center; gap: 8px; font-weight: 600; color: #115E59; }
.disc-banner__head i { font-size: 1rem; }
.disc-banner__sub { font-size: 0.8rem; color: #0F766E; margin: 3px 0 10px 26px; }
.disc-counts { display: flex; gap: 10px; flex-wrap: wrap; margin-left: 26px; }
.disc-count { background: #FFF; border: 1px solid #CCFBF1; border-radius: 6px; padding: 8px 12px; min-width: 96px; }
.disc-count__value { font-size: 1.25rem; font-weight: 700; color: #134E4A; line-height: 1.1; }
.disc-count__label { font-size: 0.66rem; text-transform: uppercase; letter-spacing: 0.04em; color: #0F766E; margin-top: 2px; }
.disc-count--eligible { border-color: #5EEAD4; background: #F0FDFA; }
.golive-panel {
  border: 1px solid #6EE7B7; background: #ECFDF5; border-radius: var(--m-radius, 8px);
  padding: 14px 16px; margin: 10px 0 4px;
}
.golive-panel__title { font-weight: 600; color: #065F46; margin-bottom: 6px; }
.golive-panel__note { font-size: 0.85rem; color: #047857; margin-bottom: 12px; }

/* Branch node */
.branch-card {
  border: 1px solid var(--m-status-info, #3B82F6); border-radius: var(--m-radius, 6px);
  padding: 10px 12px; margin: 6px 0; background: var(--m-bg-subtle, #F8FAFC);
}
.branch-card__head { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.branch-card__arms { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-top: 10px; }
.branch-arm { border-left: 3px solid var(--m-border, #E2E8F0); padding-left: 10px; }
.branch-arm--yes { border-left-color: var(--m-status-success, #10B981); }
.branch-arm--no  { border-left-color: var(--m-status-warning, #F59E0B); }
.branch-arm__label { font-size: 0.72rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.03em; margin-bottom: 6px; }
.step-row--arm { padding: 4px 0; }
@media (max-width: 640px) { .branch-card__arms { grid-template-columns: 1fr; } }

/* Flow list — performance cards */
.flow-grid__status { padding: 24px 4px; text-align: center; }
.flow-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: var(--m-space-4, 16px);
}
.flow-card {
  background: var(--m-bg-card, #FFF);
  border: 1px solid var(--m-border, #E2E8F0);
  border-radius: var(--m-radius, 10px);
  box-shadow: var(--m-shadow-card, 0 1px 2px rgba(15, 23, 42, .05));
  padding: 16px 18px;
  cursor: pointer;
  transition: box-shadow .15s, transform .15s, border-color .15s;
}
.flow-card:hover {
  box-shadow: var(--m-shadow-panel, 0 12px 32px rgba(15, 23, 42, .09));
  transform: translateY(-2px);
  border-color: var(--m-border-strong, #C7CEDC);
}
.flow-card:focus-visible { outline: 2px solid var(--m-accent, #4F46E5); outline-offset: 2px; }
.flow-card__row { display: flex; align-items: center; gap: 9px; }
.flow-card h4 { font-size: 0.93rem; font-weight: 600; letter-spacing: -0.01em; line-height: 1.3; margin: 0; flex: 1; }
.flow-card .status-pill { margin-left: 0; flex: 0 0 auto; }
.flow-card__sub { font-size: 0.74rem; color: var(--m-text-tertiary, #94A3B8); margin-top: 3px; }
.flow-card__stats {
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 6px;
  margin-top: 14px; padding-top: 13px; border-top: 1px solid var(--m-border, #F1F5F9);
}
.flow-card__stat .v { font-size: 0.98rem; font-weight: 800; letter-spacing: -0.02em; color: var(--m-text-tertiary, #94A3B8); }
.flow-card__stat .k { font-size: 0.6rem; font-weight: 700; letter-spacing: 0.09em; text-transform: uppercase; color: var(--m-text-tertiary, #94A3B8); margin-top: 1px; }
.flow-card__meta { display: flex; justify-content: space-between; gap: 8px; font-size: 0.7rem; color: var(--m-text-tertiary, #94A3B8); margin-top: 10px; }

/* Website-visit trigger config panel (Visitor-ID slice 2) */
.sv-cfg { border: 1px solid var(--m-border, #EAECF3); border-radius: 12px; padding: 18px 20px; background: var(--m-bg-card, #fff); }
.sv-cfg__head { display: flex; gap: 12px; align-items: flex-start; margin-bottom: 16px; }
.sv-cfg__head > i { color: var(--m-accent, #4F46E5); font-size: 1.1rem; margin-top: 2px; }
.sv-cfg__title { font-size: 1rem; font-weight: 700; display: flex; align-items: center; gap: 8px; }
.sv-new { font-size: 0.56rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.04em; color: #fff; background: var(--m-accent-2, #0EA5A4); padding: 2px 7px; border-radius: 999px; }
.sv-cfg__sub { font-size: 0.82rem; color: var(--m-text-secondary, #697086); line-height: 1.5; margin-top: 3px; }
.sv-field { margin-bottom: 16px; }
.sv-foot { font-size: 0.75rem; color: var(--m-text-tertiary, #9AA0AC); line-height: 1.45; margin-top: 6px; }
.sv-seg { display: inline-flex; border: 1px solid var(--m-border, #EAECF3); border-radius: 8px; overflow: hidden; }
.sv-seg button { border: none; background: var(--m-bg-card, #fff); padding: 7px 14px; font-size: 0.8rem; font-family: inherit; cursor: pointer; color: var(--m-text-secondary, #697086); border-right: 1px solid var(--m-border, #EAECF3); }
.sv-seg button:last-child { border-right: none; }
.sv-seg button.on { background: var(--m-accent, #4F46E5); color: #fff; font-weight: 600; }
.sv-toggle-row { display: flex; align-items: flex-start; gap: 12px; padding: 14px 16px; border: 1px solid var(--m-border, #EAECF3); border-radius: 10px; background: #FBFCFE; }
.sv-switch { position: relative; width: 40px; height: 22px; flex: none; margin-top: 1px; }
.sv-switch input { opacity: 0; width: 0; height: 0; }
.sv-switch .track { position: absolute; inset: 0; background: #CBD2DE; border-radius: 999px; transition: 0.15s; cursor: pointer; }
.sv-switch .track::before { content: ""; position: absolute; left: 2px; top: 2px; width: 18px; height: 18px; background: #fff; border-radius: 50%; transition: 0.15s; box-shadow: 0 1px 2px rgba(0,0,0,0.2); }
.sv-switch input:checked + .track { background: var(--m-accent, #4F46E5); }
.sv-switch input:checked + .track::before { transform: translateX(18px); }
.sv-toggle-title { font-weight: 600; font-size: 0.86rem; }
.sv-default { font-size: 0.62rem; color: var(--m-text-tertiary, #9AA0AC); font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; margin-left: 6px; }
.sv-toggle-desc { font-size: 0.79rem; color: var(--m-text-secondary, #697086); margin-top: 2px; line-height: 1.5; }
.sv-caution { display: flex; gap: 8px; align-items: flex-start; margin-top: 10px; padding: 10px 12px; background: #FFF3F5; border: 1px solid #F6D5DC; border-radius: 8px; font-size: 0.78rem; color: #9F1239; line-height: 1.45; }
.sv-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
</style>
